import { outcomeToStatusFilter } from '@flakiness/server/common/fql/extractor.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { html, nothing, TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { api } from '../api.js';
import { DataColumn, DataTableProvider } from '../components/data-table.js';
import { renderRelativeDate } from '../fragments.js';
import { PagePull } from '../page-pull.js';
import { PageRun } from '../page-run.js';
import { forgeUtils } from '../forgeUtils.js';

export function isGoodToGo(pr: WireTypes.PullRequestStats) {
  return pr.commitStats && pr.commitStats.testStats.regressed === 0 && pr.commitStats.testStats.unexpected === 0 && !TestOutcomes.isEmptyOutcomeCounts(pr.commitStats.testStats);
}

export function hasRegressions(pr: WireTypes.PullRequestStats) {
  return pr.commitStats && pr.commitStats.testStats.regressed > 0;
}

function createNameColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.PullRequestStats, WireTypes.PullRequestSortAxis> {
  return {
    sortAxis: undefined,
    width: '1fr',
    renderHeader() { return html`${forgeUtils.pullRequestLabel(project)}`; },
    renderElement(pr): TemplateResult {
      const tag = isGoodToGo(pr) ? html`<sl-tag size=small pill variant=success>Mergeable</sl-tag>` :
        hasRegressions(pr) ? html`<sl-tag pill size=small  variant=danger>Has Regressions</sl-tag>` :
        nothing;

      return html`
        <h-box style=${styleMap({
          'padding': 'var(--sl-spacing-small) 0',
          'width': '100%',
        })}>
          <fk-pr-icon style=${styleMap({
            'font-size': 'var(--sl-font-size-large)',
          })} .state=${pr.state}></fk-pr-icon>

          <v-box style="gap: var(--sl-spacing-2x-small)">
            <h-box style=${styleMap({
              'font-weight': 'var(--sl-font-weight-semibold)',
            })}>
              <a href=${PagePull.url({ orgSlug: project.org.orgSlug, projectSlug: project.projectSlug, pullRequest: pr.number, snapshotId })}>
              ${pr.title}
              </a>
              ${tag}
            </h-box>
            <h-box style=${styleMap({
              gap: `var(--sl-spacing-2x-small)`,
              'font-size': 'var(--sl-font-size-small)',
              'color': 'var(--sl-color-neutral-600)',
            })}>
              <div>
                ${forgeUtils.hasSourceUrl(project) ? html`
                  <a href=${forgeUtils.pullRequestUrl(project, String(pr.number))} target=_blank title=${`View on ${forgeUtils.providerName(project)}`}>#${pr.number}</a>
                ` : html`#${pr.number}`}
                opened ${renderRelativeDate(pr.createdTimestamp)}
                by 
                <sl-avatar image=${pr.avatar_url} label=${pr.author} loading=lazy style=${styleMap({
                  '--size': '16px',
                })}></sl-avatar>
                ${pr.author}
              </div>
              <sl-icon name=dot></sl-icon>
              <runstats-selector
                .commitStats=${pr.commitStats}
                .runlinker=${PageRun.linkifier(project, snapshotId)}
              ></runstats-selector>

            </h-box>
          </v-box>
          
        </h-box>
      `;
    },
  };
}

function createOutcomesColumn(project: WireTypes.Project, snapshotId: WireTypes.DataSnapshotId): DataColumn<WireTypes.PullRequestStats, WireTypes.PullRequestSortAxis> {
  return {
    sortAxis: undefined,
    width: 'auto',
    renderHeader() { return html`Test Results`; },
    renderElement(pr): TemplateResult {
      const linkifier = (outcome: WireTypes.Outcome) => PagePull.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        pullRequest: pr.number,
        query: `status:${outcomeToStatusFilter(outcome)}`,
        snapshotId,
      });
      return html`
        <outcomes-bar
          style="width: 100%;"
          .counts=${pr.commitStats?.testStats}
          .linkifier=${linkifier}
        ></outcomes-bar>
      `;
    },
  };
}

const DEFAULT_SORT_OPTIONS = {
  axis: 'created' as WireTypes.PullRequestSortAxis,
  direction: 'desc' as WireTypes.SortDirection,
}

export function createPullsDataProvider(options: {
  project: WireTypes.Project,
  snapshotId: WireTypes.DataSnapshotId,
  prState: WireTypes.PullRequestState,
  testState: 'all' | 'tested' | 'untested',
  timezone: string,
}): DataTableProvider<WireTypes.PullRequestStats, WireTypes.PullRequestSortAxis> {
  const name = createNameColumn(options.project, options.snapshotId);
  return {
    loader: (pageOptions, sortOptions, signal) => api.project.pulls.GET({
      orgSlug: options.project.org.orgSlug,
      projectSlug: options.project.projectSlug,
      snapshotId: options.snapshotId,
      prState: options.prState,
      testState: options.testState,
      sortOptions: sortOptions ?? DEFAULT_SORT_OPTIONS,
      pageOptions,
    }),
    rowOutcome: pr => {
      if (isGoodToGo(pr))
        return 'expected';
      if (hasRegressions(pr))
        return 'regressed';
      return undefined;
    },
    columns: [name, createOutcomesColumn(options.project, options.snapshotId)],
    defaultSortColumn: name,
  };
}
